import React from 'react';
import { X, Eraser, Pencil, Pipette, Undo2 } from 'lucide-react';
import type { DrawTool, UseDrawCanvasReturn } from '../hooks/useDrawCanvas';
import { useExclusivePopover } from '../hooks/useExclusivePopover';

const COLORS = ['#ef4444', '#f59e0b', '#22c55e', '#3b82f6', '#8b5cf6', '#ffffff', '#000000'];

interface PaintPopoverProps {
  draw: UseDrawCanvasReturn;
  onClose: () => void;
  placement?: 'left' | 'right' | 'bottom';
}

const TOOLS: { id: DrawTool; icon: React.FC<{ size?: number }>; label: string }[] = [
  { id: 'eyedropper', icon: Pipette, label: '滴管' },
  { id: 'brush', icon: Pencil, label: '畫筆' },
  { id: 'eraser', icon: Eraser, label: '橡皮擦' },
];

const PaintPopover: React.FC<PaintPopoverProps> = ({ draw, onClose, placement = 'left' }) => {
  useExclusivePopover(onClose);
  const btnBase = 'w-8 h-8 flex items-center justify-center rounded-lg transition-all text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-700';
  const btnActive = 'bg-indigo-600 !text-white shadow-sm';
  // 'bottom' 用於手機橫向 toolbar：以 fixed 形式從畫面底部彈出，避開 toolbar 寬度限制
  const isBottom = placement === 'bottom';
  const placementClass =
    placement === 'left' ? 'absolute right-full mr-2 top-0 w-64' :
    placement === 'right' ? 'absolute left-full ml-2 top-0 w-64' :
    'fixed left-4 right-4 bottom-4 mx-auto max-w-md';

  return (
    <div
      className={`${placementClass} z-50 ${isBottom ? 'animate-in slide-in-from-bottom-4 duration-200 pt-2 shadow-2xl' : 'max-w-[calc(100vw-2rem)]'} bg-white/95 dark:bg-slate-900/95 backdrop-blur-md border border-slate-200 dark:border-slate-700 rounded-2xl shadow-xl p-3 flex flex-col gap-3`}
      onClick={e => e.stopPropagation()}
    >
      {/* 畫筆/滴管/橡皮擦需在彈窗開啟時操作圖片，因此不加 backdrop 也不可下滑關閉 */}
      {isBottom && (
        <div className="flex justify-center -mt-1">
          <div className="w-10 h-1 rounded-full bg-slate-300 dark:bg-slate-600" />
        </div>
      )}
      <div className="flex items-center justify-between">
        <span className="text-xs font-bold text-slate-700 dark:text-slate-200">畫筆工具</span>
        <button
          className="text-slate-400 hover:text-slate-700 dark:hover:text-white transition-colors"
          onClick={onClose}
          title="關閉畫筆模式"
        >
          <X size={14} />
        </button>
      </div>

      {/* 工具：滴管 / 畫筆 / 橡皮擦 */}
      <div className="flex items-center gap-1.5">
        <span className="text-[10px] text-slate-400 w-9 shrink-0">工具</span>
        <div className="flex gap-1">
          {TOOLS.map(({ id, icon: Icon, label }) => (
            <button
              key={id}
              type="button"
              title={label}
              onClick={() => draw.setTool(id)}
              className={`${btnBase} ${draw.tool === id ? btnActive : ''}`}
            >
              <Icon size={15} />
            </button>
          ))}
        </div>
      </div>

      {/* 顏色 */}
      <div className="flex items-center gap-1.5">
        <span className="text-[10px] text-slate-400 w-9 shrink-0">顏色</span>
        <div className="flex gap-1 items-center flex-wrap">
          {COLORS.map(c => (
            <button
              key={c}
              type="button"
              title={c}
              onClick={() => draw.setColor(c)}
              className="w-5 h-5 rounded transition-transform hover:scale-110"
              style={{
                background: c,
                boxShadow: draw.color === c
                  ? '0 0 0 2px #6366f1'
                  : c === '#ffffff' ? '0 0 0 1px #94a3b8' : 'none',
              }}
            />
          ))}
        </div>
      </div>

      {/* 粗細 */}
      <div className="flex items-center gap-2">
        <span className="text-[10px] text-slate-400 w-9 shrink-0">粗細</span>
        <input
          type="range"
          min={1}
          max={20}
          value={draw.lineWidth}
          onChange={e => draw.setLineWidth(Number(e.target.value))}
          className="flex-1 accent-indigo-500"
        />
        <span className="text-[10px] text-slate-400 w-5">{draw.lineWidth}</span>
      </div>

      {/* 復原 */}
      <button
        type="button"
        disabled={draw.historyLength === 0}
        onClick={draw.undo}
        className="w-full py-2 flex items-center justify-center gap-1.5 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 disabled:opacity-30 disabled:cursor-not-allowed text-slate-600 dark:text-slate-300 text-xs font-medium rounded-xl transition-all"
      >
        <Undo2 size={13} />
        復原一步
      </button>
    </div>
  );
};

export default PaintPopover;
